import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { UserPlus, Mail, Building, Shield, Briefcase, Copy, Check, AlertCircle, Send } from 'lucide-react';
import { AdminCalendarFeedStatus } from './CalendarSync';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIDEBAR_OPTIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'my_schedule', label: 'My Schedule' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'schedule_insights', label: 'Schedule Insights' },
  { key: 'shift_swaps', label: 'Shift Swaps' },
  { key: 'pto', label: 'PTO Management' },
  { key: 'reports', label: 'Reports' },
  { key: 'admin', label: 'Admin' },
  { key: 'team', label: 'Team' },
  { key: 'audit_log', label: 'Audit Log' },
  { key: 'help', label: 'Help' },
];

const EMPTY = {
  email: '', username: '', name: '', role: '',
  has_schedule: true, work_location: 'office',
  sidebar_access: null,
  team_id: '', manager_id: '', managed_roles: [],
  work_phone: '', home_phone: '', job_title: '',
  hire_date: '', birthday: '',
  hourly_rate: '', salary: '', employment_type: '', overtime_rate_multiplier: '',
};

export default function UserFormDialog({
  open,
  onOpenChange,
  initial,           // existing user to edit, or null for create
  systemRoles = [],
  teams = [],
  users = [],
  onSaved,           // (userResult) => void
}) {
  const isEdit = !!initial?.id;
  const [tab, setTab] = useState('account');
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { invite_url, email_sent, ... }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setTab('account');
      setInviteResult(null);
      setCopied(false);
      if (initial?.id) {
        setForm({
          ...EMPTY,
          ...initial,
          managed_roles: initial.managed_roles || [],
          sidebar_access: initial.sidebar_access || null,
          has_schedule: initial.has_schedule !== false,
          work_location: initial.work_location || 'office',
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, initial]);

  const updateForm = (patch) => setForm((p) => ({ ...p, ...patch }));

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const managersList = useMemo(
    () => users.filter((u) => ['admin', 'manager', 'team_lead'].includes(u.role) && u.id !== form.id),
    [users, form.id]
  );

  const validateAccountTab = () => {
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error('Please enter a valid email address.');
      setTab('account');
      return false;
    }
    if (!form.name?.trim()) {
      toast.error('Full name is required.');
      setTab('account');
      return false;
    }
    if (!form.role) {
      toast.error('Please select a role.');
      setTab('account');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateAccountTab()) return;
    setSaving(true);
    try {
      const headers = authHeaders();
      if (isEdit) {
        await axios.put(`${API}/users/${initial.id}`, {
          username: form.username || (form.email || '').split('@')[0],
          email: form.email,
          role: form.role,
          name: form.name,
          has_schedule: form.has_schedule,
          work_location: form.work_location,
          sidebar_access: form.sidebar_access,
          team_id: form.team_id || null,
          manager_id: form.manager_id || null,
          managed_roles: form.managed_roles || [],
          work_phone: form.work_phone || null,
          home_phone: form.home_phone || null,
          job_title: form.job_title || null,
          hire_date: form.hire_date || null,
          birthday: form.birthday || null,
          hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
          salary: form.salary ? parseFloat(form.salary) : null,
          employment_type: form.employment_type || null,
          overtime_rate_multiplier: form.overtime_rate_multiplier ? parseFloat(form.overtime_rate_multiplier) : null,
        }, { headers });
        toast.success('User updated.');
        onSaved?.({ updated: true });
        onOpenChange(false);
      } else {
        const res = await axios.post(`${API}/users/invite`, {
          email: form.email,
          username: form.username || undefined,
          name: form.name,
          role: form.role,
          has_schedule: form.has_schedule,
          work_location: form.work_location,
          sidebar_access: form.sidebar_access,
          team_id: form.team_id || null,
          manager_id: form.manager_id || null,
          managed_roles: form.managed_roles || [],
          work_phone: form.work_phone || null,
          home_phone: form.home_phone || null,
          job_title: form.job_title || null,
          hire_date: form.hire_date || null,
          birthday: form.birthday || null,
          hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
          salary: form.salary ? parseFloat(form.salary) : null,
          employment_type: form.employment_type || null,
          overtime_rate_multiplier: form.overtime_rate_multiplier ? parseFloat(form.overtime_rate_multiplier) : null,
        }, { headers });
        setInviteResult(res.data);
        if (res.data.email_sent) {
          toast.success(`User created — welcome email sent to ${res.data.email}.`);
        } else {
          toast.warning('User created, but the welcome email could not be delivered. Use the manual link below.');
        }
        onSaved?.(res.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save user.');
    } finally {
      setSaving(false);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteResult?.invite_url) return;
    try {
      await navigator.clipboard.writeText(inviteResult.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success('Invite link copied to clipboard.');
    } catch {
      toast.error('Copy failed — select and copy manually.');
    }
  };

  // Once an invite is created, show a "success" screen with the link
  if (inviteResult) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="user-invite-success">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-emerald-600" /> User created
            </DialogTitle>
            <DialogDescription>
              {inviteResult.email_sent ? (
                <>A welcome email was sent to <strong>{inviteResult.email}</strong>. They'll click "Set up your account" to choose a password and log in.</>
              ) : (
                <>The user was created, but the welcome email couldn't be delivered. Share the link below with them manually.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {!inviteResult.email_sent && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  Email delivery failed{inviteResult.email_enabled ? '' : ' (Resend not configured for this environment)'}.
                  Share this set-password link with the new user — it expires in {inviteResult.invite_expires_in_days || 7} days.
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
              <Input readOnly value={inviteResult.invite_url} className="font-mono text-xs" data-testid="invite-url-input" />
              <Button variant="outline" size="sm" onClick={copyInviteLink} data-testid="copy-invite-btn">
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => onOpenChange(false)} data-testid="invite-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-blue-600" />
            {isEdit ? `Edit ${initial?.name || initial?.username || 'User'}` : 'Invite a New User'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update this user's profile, access, and org details."
              : "Fill in the basics, then we'll email them a link to set their own password. They'll be in your team as soon as they accept."}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="account" data-testid="tab-account">
              <Mail className="h-4 w-4 mr-1.5" /> Account
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">
              <Briefcase className="h-4 w-4 mr-1.5" /> Profile
            </TabsTrigger>
            <TabsTrigger value="org" data-testid="tab-org">
              <Building className="h-4 w-4 mr-1.5" /> Org
            </TabsTrigger>
            <TabsTrigger value="access" data-testid="tab-access">
              <Shield className="h-4 w-4 mr-1.5" /> Access
            </TabsTrigger>
          </TabsList>

          {/* ACCOUNT */}
          <TabsContent value="account" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="uf-email">Email *</Label>
                <Input id="uf-email" type="email" autoComplete="off" value={form.email} onChange={(e) => updateForm({ email: e.target.value })} placeholder="alex@yourcompany.com" data-testid="uf-email" disabled={isEdit} />
                {isEdit && <p className="text-[11px] text-gray-400">Email is fixed once a user has been created.</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="uf-name">Full Name *</Label>
                <Input id="uf-name" value={form.name} onChange={(e) => updateForm({ name: e.target.value })} placeholder="Alex Morgan" data-testid="uf-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="uf-username">Username (optional)</Label>
                <Input id="uf-username" value={form.username} onChange={(e) => updateForm({ username: e.target.value })} placeholder="auto-derived from email" data-testid="uf-username" />
              </div>
              <div className="space-y-1">
                <Label>Role *</Label>
                <Select value={form.role} onValueChange={(v) => updateForm({ role: v })}>
                  <SelectTrigger data-testid="uf-role"><SelectValue placeholder="Select a role" /></SelectTrigger>
                  <SelectContent>
                    {systemRoles.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-lg bg-blue-50/50 border border-blue-100 p-3 flex items-start gap-3">
              <Send className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-900">
                <strong>{isEdit ? 'Note:' : 'After you save'}:</strong>{' '}
                {isEdit
                  ? "Changes apply immediately. The user keeps their existing password."
                  : "we'll email this person a link to set their own password. The link expires in 7 days. If email delivery fails, we'll show you a copyable link to share manually."}
              </div>
            </div>
            {isEdit && <AdminCalendarFeedStatus userId={initial.id} />}
          </TabsContent>

          {/* PROFILE */}
          <TabsContent value="profile" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="uf-jt">Job Title</Label>
                <Input id="uf-jt" value={form.job_title} onChange={(e) => updateForm({ job_title: e.target.value })} placeholder="Customer Support Manager" data-testid="uf-job-title" />
              </div>
              <div className="space-y-1">
                <Label>Work Location</Label>
                <Select value={form.work_location || 'office'} onValueChange={(v) => updateForm({ work_location: v })}>
                  <SelectTrigger data-testid="uf-work-location"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="office">🏢 Office Employee</SelectItem>
                    <SelectItem value="remote">🏠 Remote Employee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="uf-wphone">Work Phone</Label>
                <Input id="uf-wphone" value={form.work_phone} onChange={(e) => updateForm({ work_phone: e.target.value })} placeholder="412-555-1234" data-testid="uf-work-phone" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="uf-hphone">Personal / Mobile</Label>
                <Input id="uf-hphone" value={form.home_phone} onChange={(e) => updateForm({ home_phone: e.target.value })} placeholder="412-555-5678" data-testid="uf-home-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="uf-hire">Hire Date</Label>
                <Input id="uf-hire" type="date" value={form.hire_date || ''} onChange={(e) => updateForm({ hire_date: e.target.value })} data-testid="uf-hire-date" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="uf-bday">Birthday (M/D)</Label>
                <Input id="uf-bday" type="date" value={form.birthday || ''} onChange={(e) => updateForm({ birthday: e.target.value })} data-testid="uf-birthday" />
              </div>
            </div>
            <details className="rounded-lg border border-gray-200 p-3 group">
              <summary className="text-sm font-medium text-gray-700 cursor-pointer flex items-center justify-between">
                <span>Compensation (optional)</span>
                <span className="text-xs text-gray-400 group-open:hidden">Click to expand</span>
              </summary>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1">
                  <Label htmlFor="uf-hr">Hourly Rate</Label>
                  <Input id="uf-hr" type="number" step="0.01" value={form.hourly_rate} onChange={(e) => updateForm({ hourly_rate: e.target.value })} placeholder="25.00" data-testid="uf-hourly-rate" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="uf-sal">Annual Salary</Label>
                  <Input id="uf-sal" type="number" step="100" value={form.salary} onChange={(e) => updateForm({ salary: e.target.value })} placeholder="65000" data-testid="uf-salary" />
                </div>
                <div className="space-y-1">
                  <Label>Employment Type</Label>
                  <Select value={form.employment_type || ''} onValueChange={(v) => updateForm({ employment_type: v })}>
                    <SelectTrigger data-testid="uf-employment-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full-Time</SelectItem>
                      <SelectItem value="part_time">Part-Time</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="uf-ot">OT Multiplier</Label>
                  <Input id="uf-ot" type="number" step="0.1" value={form.overtime_rate_multiplier} onChange={(e) => updateForm({ overtime_rate_multiplier: e.target.value })} placeholder="1.5" data-testid="uf-ot-multiplier" />
                </div>
              </div>
            </details>
          </TabsContent>

          {/* ORG */}
          <TabsContent value="org" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Team</Label>
                <Select value={form.team_id || 'none'} onValueChange={(v) => updateForm({ team_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="uf-team"><SelectValue placeholder="Select team (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Direct Manager</Label>
                <Select value={form.manager_id || 'none'} onValueChange={(v) => updateForm({ manager_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="uf-manager"><SelectValue placeholder="Select manager (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {managersList.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name || u.username} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {['team_lead', 'manager', 'admin'].includes(form.role) && (
              <div className="rounded-lg bg-purple-50/40 border border-purple-100 p-3 space-y-2">
                <Label>Managed Role Teams</Label>
                <p className="text-xs text-gray-500">Which role teams does this {form.role} manage? They'll appear in those role schedules.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                  {systemRoles.filter((r) => !['admin', 'manager', 'team_lead'].includes(r.id)).map((r) => {
                    const checked = (form.managed_roles || []).includes(r.id);
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => updateForm({ managed_roles: checked ? form.managed_roles.filter((x) => x !== r.id) : [...(form.managed_roles || []), r.id] })}
                        className={`text-xs px-3 py-1.5 rounded-md border transition-colors text-left ${checked ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'}`}
                        data-testid={`uf-managed-${r.id}`}
                      >
                        {r.name}
                      </button>
                    );
                  })}
                </div>
                {form.managed_roles?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {form.managed_roles.map((rid) => {
                      const r = systemRoles.find((x) => x.id === rid);
                      return r ? <Badge key={rid} variant="secondary" className="text-xs">{r.name}</Badge> : null;
                    })}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* ACCESS */}
          <TabsContent value="access" className="space-y-4 pt-4">
            <div className="flex items-center justify-between p-3 bg-blue-50/40 rounded-lg border border-blue-100">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Appears in Schedule</Label>
                <p className="text-xs text-gray-500">Enable for employees who can be assigned shifts. Disable for view-only roles (observers, certain admins).</p>
              </div>
              <Switch checked={form.has_schedule !== false} onCheckedChange={(v) => updateForm({ has_schedule: v })} data-testid="uf-has-schedule" />
            </div>

            {form.role && form.role !== 'admin' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Page Access</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={() => updateForm({ sidebar_access: ['my_schedule', 'help'] })}>Basic Only</Button>
                    <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={() => updateForm({ sidebar_access: null })}>Full Access</Button>
                  </div>
                </div>
                <p className="text-xs text-gray-500">Pick which sidebar pages this user can see. Click "Full Access" to remove all restrictions.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                  {SIDEBAR_OPTIONS.map((opt) => {
                    const access = form.sidebar_access;
                    const isOn = !access || access.includes(opt.key);
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => {
                          const current = form.sidebar_access || SIDEBAR_OPTIONS.map((o) => o.key);
                          const updated = isOn ? current.filter((k) => k !== opt.key) : [...current, opt.key];
                          updateForm({ sidebar_access: updated.length === 0 ? ['my_schedule'] : updated });
                        }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all border ${isOn ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-500 border-gray-200 hover:border-blue-300'}`}
                        data-testid={`uf-sidebar-${opt.key}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {form.sidebar_access && (
                  <p className="text-xs text-amber-600">Restricted to {form.sidebar_access.length} page(s).</p>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="uf-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="uf-save">
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create & Send Invite')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
